import { Injectable } from '@angular/core';
import * as localforage from 'localforage';
import { createStore, withProps, setProp, select } from '@ngneat/elf';
import {
  withEntities,
  selectAllEntities,
  setEntities,
  addEntities,
  deleteEntities,
  updateEntities,
  getEntity,
} from '@ngneat/elf-entities';
import { withPagination } from '@ngneat/elf-pagination';
import { persistState } from '@ngneat/elf-persist-state';
import * as moment from 'moment';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { IMusic } from './music.interface';
import {
  IListParams,
  IPaginationState,
  IScrollPosition,
  IStoreProps,
} from 'src/app/talent-litmus-core/common/common.interface';
import { ApiBaseService } from 'src/app/shared/services/api/api-base/api-base.service';
import { REFRESH_TIME_IN_MINUTES } from 'src/app/shared/configs/global-values.constants';
import { PageEvent } from '@angular/material/paginator';

// eslint-disable-next-line @typescript-eslint/naming-convention
const STORE_NAME = 'lvl1-music';

const instance = localforage.createInstance({
  driver: localforage.INDEXEDDB,
  name: 'talent-litmus-adminv2',
  storeName: STORE_NAME,
});

const store = createStore(
  { name: STORE_NAME },
  withProps<IStoreProps>({ lastRefreshed: undefined }),
  withProps<IPaginationState>({
    length: 0,
    pageIndex: 0,
    pageSize: 0,
    previousPageIndex: 0,
  }),
  withEntities<IMusic, '_id'>({ idKey: '_id' }),
  withProps<IScrollPosition>({ x: 0, y: 0 }),
  withProps<{ selectedId: string }>({ selectedId: null }),
  withProps<{ searchParam: string }>({ searchParam: '' }),
  withPagination(),
);

export const persist = persistState(store, {
  key: STORE_NAME,
  storage: instance,
});

@Injectable()
export class MusicCoreService {
  private _loading$ = new BehaviorSubject<boolean>(false);

  /**
   * @name musicList$
   * @description emits stored list of the music
   */
  musicList$ = store.pipe(selectAllEntities());
  paginationState$ = store.pipe(
    select((state) => {
      return state;
    }),
  );
  totalCount: number;

  public get loading$(): Observable<boolean> {
    return this._loading$.asObservable();
  }

  constructor(private _api: ApiBaseService) {
    // store get it previous state after a moment, That is why It's initial state is undefined/null.
    // and after a moment it accesses its previous state.
    if (!store.value.lastRefreshed) {
      setTimeout(() => {
        this.refresh();
      }, 300);
    } else {
      this.refresh();
    }
  }

  public getMusic(query?: IListParams): void {
    this._loading$.next(true);
    this._api
      .get('music/list', query || {})
      .subscribe((data: IMusicListResponse) => {
        this.totalCount = data.totalCount;
        store.update(
          setEntities(data.list),
          setProp('lastRefreshed', new Date()),
        );
        this._loading$.next(false);
      });
  }

  public addMusic(music: IMusic): Observable<any> {
    this._loading$.next(true);
    return this._api.post('music/add', music, {}).pipe(
      tap((music: IMusic) => {
        store.update(addEntities(music));
        this._loading$.next(false);
      }),
    );
  }

  public editMusic(data): Observable<any> {
    this._loading$.next(true);
    return this._api.put('music/edit', data, {}).pipe(
      map(() => {
        store.update(updateEntities(data['_id'], data));
        this._loading$.next(false);
      }),
    );
  }

  public deleteMusic(data): Observable<any> {
    this._loading$.next(true);
    return this._api.delete('music', { body: data }).pipe(
      tap(() => {
        store.update(deleteEntities(data['_id']));
        this._loading$.next(false);
      }),
    );
  }

  public uploadMusic(formData): Observable<any> {
    return this._api.post('music/file/upload', formData, {
      responseType: 'text',
    });
  }

  public deleteMusicFile(data): Observable<any> {
    return this._api.delete('music/file', { body: data });
  }

  public getMusicInfo(id: string): IMusic {
    const music = store.query(getEntity(id));
    return music;
  }

  moveToLastPage(): void {
    let length: number;
    let pageSize: number;
    let previousPageIndex: number;
    this.paginationState$.subscribe((state) => {
      length = state.length;
      pageSize = state.pageSize;
      previousPageIndex = state.previousPageIndex;
    });
    this.pageChangeEvent({
      length,
      pageIndex: Math.ceil(length / pageSize),
      pageSize,
      previousPageIndex,
    });
  }

  pageChangeEvent(event: PageEvent): void {
    store.update((state) => ({
      ...state,
      length: event.length,
      pageIndex: event.pageIndex,
      pageSize: event.pageSize,
      previousPageIndex: event.previousPageIndex,
    }));
  }

  setSearchParam(searchParam: string): void {
    store.update((state) => ({
      ...state,
      searchParam: searchParam,
    }));
  }

  setScrollPosition(scrollPostion: [number, number]): void {
    store.update((state) => ({
      ...state,
      x: scrollPostion[0],
      y: scrollPostion[1],
    }));
  }

  setSelectedId(id: string): void {
    store.update((state) => ({
      ...state,
      selectedId: id,
    }));
  }

  public refresh(forced?: boolean): void {
    if (
      forced ||
      !store.value.lastRefreshed ||
      moment().diff(moment(store.value.lastRefreshed), 'minutes') >
        REFRESH_TIME_IN_MINUTES
    ) {
      this.getMusic();
    }
  }
}

interface IMusicListResponse {
  list: IMusic[];
  totalCount: number;
}
